import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { withStyles } from '@mui/styles';
import {
  Autocomplete, Avatar, Box, Button, FormControlLabel, Grid, IconButton, Skeleton, Switch, TextField, Tooltip, Typography
} from '@mui/material';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import moment from 'moment';
import PhoneIcon from '@mui/icons-material/Phone';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import CallMadeIcon from '@mui/icons-material/CallMade';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import DialpadIcon from '@mui/icons-material/Dialpad';
import HistoryIcon from '@mui/icons-material/History';
import PeopleAltIcon from '@mui/icons-material/PeopleAltOutlined';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import { Styles } from '../../../../styles/material-styles';
import { vt, cardSx } from '../../../../styles/voip-theme';
import voipService from '../../../../services/voip.service';
import VoipCallDetailsModal from '../../../../components/voip/voip-call-details-modal';
import VoipDialConfirm from '../../../../components/voip/voip-dial-confirm';

// =====================================================================
// VoipCallPanel — aba "Telefonia" DENTRO do chamado. DADOS REAIS.
// Esquerda: histórico/evidências deste chamado (GET /voip/calls?calledId=).
// Direita (fixo): bloco do agente — ramal real (getExtensions) + Discar (delega
// pro widget) + Recentes (GET /voip/calls). Skeleton enquanto carrega.
// SEM async/await (babel 7.12.3 do react-scripts 4 quebra com arrow async).
// =====================================================================

const applyDdi = (number, country, useDdi) => {
  const n = String(number || '');
  if (useDdi) return n;
  const dc = country && country.dialCode ? String(country.dialCode) : '';
  return dc && n.startsWith(dc) ? n.slice(dc.length) : n;
};
const fmtClock = (s) => {
  const t = Math.max(0, Math.floor(s || 0));
  return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
};
const fmtWhen = (iso) => (iso ? moment(iso).format('DD/MM/YYYY HH:mm') : '');
const callNumber = (c) => (c.direction === 'inbound' ? c.fromNumber : c.toNumber) || '—';
// Telefone válido = 10–13 dígitos (BR máx 13 com DDI). Exclui IDs da Meta (Instagram/Messenger, 15-17 dígitos).
const isValidPhone = (s) => { const n = String(s || '').replace(/\D/g, '').length; return n >= 10 && n <= 13; };
const dialViaWidget = (number, contact, calledId) => window.dispatchEvent(new CustomEvent('voip:dial', { detail: { number, contact, calledId } }));

function VoipCallPanel(props) {
  const { intl, calleds, userSession, requester } = props;
  const called = (calleds && calleds.called) || {};
  const f = (id, def) => intl.formatMessage({ id, defaultMessage: def });

  const reqUser = called.requestinguser || called.requestingUser || {};
  // requester vem do pai (index.js), carregado do /new — é a fonte confiável do telefone do solicitante.
  const contactPhone = (requester && (requester.phone || requester.mobilephone)) || reqUser.phone || reqUser.mobilephone || (called.organization && called.organization.phone) || '';
  const contactName = (requester && requester.name) || reqUser.name || (called.organization && called.organization.name) || f('voip.call.contact.unknown', 'Contato');
  const contactOrg = (called.organization && called.organization.name) || '';
  const contactPhoneDigits = String(contactPhone || '').replace(/\D/g, '');
  const contactPhoneValid = contactPhoneDigits.length >= 10;
  const ticketTitle = called.title || '';
  const ticketId = called.id || '';
  const agentName = (userSession && userSession.user && userSession.user.name) || f('voip.call.agent', 'Agente');
  const userId = userSession && userSession.user && userSession.user.id;

  const [tab, setTab] = useState('dial'); // dial | contacts | recents
  const [contactQuery, setContactQuery] = useState('');
  const [contactResults, setContactResults] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [contactSearched, setContactSearched] = useState(false);
  const [extensions, setExtensions] = useState([]);
  const [ramal, setRamal] = useState(null);
  const [loadingExt, setLoadingExt] = useState(true);
  const [voipState, setVoipState] = useState(() => (typeof window !== 'undefined' && window.__mexxVoip) || { enabled: false, hasRamal: false, ramal: null });
  const [dialConfirm, setDialConfirm] = useState({ open: false, number: '' });
  const [number, setNumber] = useState(contactPhone || '');
  const [country, setCountry] = useState({ dialCode: '55' });
  const [useDdi, setUseDdi] = useState(true);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [recents, setRecents] = useState([]);
  const [loadingRecents, setLoadingRecents] = useState(false);
  const [recentsLoaded, setRecentsLoaded] = useState(false);
  const [detailCall, setDetailCall] = useState(null);
  const mountedRef = useRef(true);

  // Recarrega o histórico de chamadas deste chamado (mount + evento 'voip:refresh-calls' do widget).
  const reloadHistory = () => {
    if (!ticketId) { setLoadingHistory(false); return; }
    voipService.getCallsByCalled(ticketId)
      .then((res) => { if (mountedRef.current) setHistory(((res && res.data) || []).map(voipService.normalizeCall)); })
      .catch(() => {})
      .then(() => { if (mountedRef.current) setLoadingHistory(false); });
  };

  // Refresh manual (botão) — atualiza sem skeleton, com spinner no ícone.
  const refreshHistory = () => {
    if (!ticketId || refreshing) return;
    setRefreshing(true);
    voipService.getCallsByCalled(ticketId)
      .then((res) => { if (mountedRef.current) setHistory(((res && res.data) || []).map(voipService.normalizeCall)); })
      .catch(() => {})
      .then(() => { if (mountedRef.current) setRefreshing(false); });
  };

  useEffect(() => {
    const onVoipState = (e) => setVoipState((e && e.detail) || window.__mexxVoip || { enabled: false, hasRamal: false, ramal: null });
    window.addEventListener('voip:state', onVoipState);
    return () => window.removeEventListener('voip:state', onVoipState);
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    // Ramais do agente logado (configs ativas → extensions filtradas por userId)
    voipService.getConfigs()
      .then((cfg) => {
        const configs = ((cfg && cfg.data) || []).map(voipService.normalizeConfig).filter((c) => c.isActive);
        return Promise.all(configs.map((c) => voipService.getExtensions(c.id).then((r) => ((r && r.data) || []).map(voipService.normalizeExtension))));
      })
      .then((lists) => {
        const all = [].concat.apply([], lists || []);
        const mine = all.filter((e) => String(e.userId) === String(userId) && e.isActive).map((e) => ({ id: e.id, ext: e.extension, name: e.userName || '', provider: e.providerName || '' }));
        if (mountedRef.current) { setExtensions(mine); setRamal(mine[0] || null); }
      })
      .catch(() => {})
      .then(() => { if (mountedRef.current) setLoadingExt(false); });

    reloadHistory();

    // Widget avisa quando uma chamada é concluída → recarrega a lista da esquerda.
    const onRefresh = () => reloadHistory();
    window.addEventListener('voip:refresh-calls', onRefresh);

    return () => { mountedRef.current = false; window.removeEventListener('voip:refresh-calls', onRefresh); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRecents = () => {
    setLoadingRecents(true);
    voipService.getRecentCalls(20)
      .then((res) => { if (mountedRef.current) { setRecents(((res && res.data) || []).map(voipService.normalizeCall)); setRecentsLoaded(true); } })
      .catch(() => {})
      .then(() => { if (mountedRef.current) setLoadingRecents(false); });
  };

  const changeTab = (k) => {
    setTab(k);
    if (k === 'recents' && !recentsLoaded) loadRecents();
  };

  // Busca de contatos (tb_users + tb_2talk_customer) ao clicar na lupa / Enter.
  const doSearchContacts = () => {
    const term = contactQuery.trim();
    if (!term) { setContactResults([]); setContactSearched(false); return; }
    setLoadingContacts(true); setContactSearched(true);
    voipService.searchContacts(term)
      .then((res) => { if (mountedRef.current) setContactResults(((res && res.data) || []).map(voipService.normalizeContact)); })
      .catch(() => { if (mountedRef.current) setContactResults([]); })
      .then(() => { if (mountedRef.current) setLoadingContacts(false); });
  };

  const contactsContent = () => (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexShrink: 0 }}>
        <TextField fullWidth size="small" placeholder={f('voip.call.searchContact', 'Buscar contato (nome ou número)')} value={contactQuery}
          onChange={(e) => setContactQuery(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') doSearchContacts(); }} sx={{ flex: 1 }} />
        <Tooltip title={f('voip.call.search', 'Buscar')}>
          <IconButton onClick={doSearchContacts} sx={{ width: 40, height: 40, flexShrink: 0, background: vt.greenGrad, '&:hover': { background: vt.greenGradHover }, '& svg': { color: '#fff !important' }, '& path': { fill: '#fff !important' } }}><SearchIcon sx={{ fontSize: 20 }} /></IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {loadingContacts ? skeletonRows(4)
          : !contactSearched ? <Typography sx={{ fontSize: 12.5, color: vt.textSoft, textAlign: 'center', py: 3 }}>{f('voip.call.searchHint', 'Digite um nome ou número e busque.')}</Typography>
            : contactResults.length === 0 ? <Typography sx={{ fontSize: 12.5, color: vt.textSoft, textAlign: 'center', py: 3 }}>{f('voip.call.noContact', 'Nenhum contato encontrado')}</Typography>
              : contactResults.map((c) => {
                const valid = c.phoneValid && isValidPhone(c.phone);
                return (
                  <Box key={`${c.source}-${c.id}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 2, border: '1px solid transparent', '&:hover': { bgcolor: vt.surfaceAlt, borderColor: vt.border } }}>
                    <Avatar sx={{ width: 34, height: 34, bgcolor: vt.greenSoft, color: 'var(--mexx-green-dark, #558B2F)', fontWeight: 700, fontSize: 14 }}>{String(c.name || '?').charAt(0).toUpperCase()}</Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: vt.text }} noWrap>{c.name}</Typography>
                      {valid ? (
                        <Typography sx={{ fontSize: 11, color: vt.textSoft }} noWrap>{c.phone} · {c.source === 'user' ? f('voip.call.srcClient', 'Cliente') : f('voip.call.srcContact', 'Contato')}</Typography>
                      ) : (
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
                          <WarningAmberIcon sx={{ fontSize: 12, color: vt.amber }} />
                          <Typography sx={{ fontSize: 11, color: vt.amber, fontWeight: 600 }}>{c.phone ? f('voip.call.invalidPhone', 'Telefone inválido') : f('voip.call.noPhone', 'Sem telefone')}</Typography>
                        </Box>
                      )}
                    </Box>
                    {valid && (
                      <Tooltip title={f('voip.call.dial', 'Ligar')}>
                        <IconButton size="small" onClick={() => dialViaWidget(c.phone, { name: c.name, org: '' }, ticketId || null)} sx={{ bgcolor: vt.greenSoft, color: 'var(--mexx-green-dark, #558B2F)', '&:hover': { bgcolor: 'var(--mexx-green-light, #E4F0D4)' } }}><PhoneIcon sx={{ fontSize: 17 }} /></IconButton>
                      </Tooltip>
                    )}
                  </Box>
                );
              })}
      </Box>
    </Box>
  );

  const statusChip = (status) => {
    const map = {
      initiated: { bg: vt.blueSoft, fg: vt.blue, label: f('voip.call.status.initiated', 'Discada') },
      ringing: { bg: vt.blueSoft, fg: vt.blue, label: f('voip.call.status.ringing', 'Chamando') },
      answered: { bg: vt.greenGrad, fg: '#fff', label: f('voip.call.status.answered', 'Atendida') },
      ended: { bg: vt.greenGrad, fg: '#fff', label: f('voip.call.status.answered', 'Atendida') },
      missed: { bg: '#FEF2F2', fg: '#B91C1C', label: f('voip.call.status.missed', 'Não atendida') },
      failed: { bg: '#F1F5F9', fg: '#475569', label: f('voip.call.status.failed', 'Falhou') },
    };
    const c = map[status] || { bg: '#F1F5F9', fg: '#475569', label: status || '—' };
    return <Box component="span" sx={{ display: 'inline-block', px: 1, py: '3px', borderRadius: 999, background: c.bg, color: c.fg, fontWeight: 700, fontSize: 11 }}>{c.label}</Box>;
  };

  const skeletonRows = (count) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {Array.from({ length: count }).map((_, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 0.5 }}>
          <Skeleton variant="circular" width={36} height={36} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="50%" height={16} />
            <Skeleton variant="text" width="75%" height={12} />
          </Box>
        </Box>
      ))}
    </Box>
  );

  // ---- item de chamada (reuso: histórico do chamado e recentes).
  // callable=true → mostra o botão do fone (SÓ ele dispara a ligação; a linha não).
  const callRow = (c, callable) => (
    <Box key={c.id}
      sx={{ p: 1.5, border: `1px solid ${vt.border}`, borderRadius: 3, transition: 'all .15s', '&:hover': { borderColor: vt.borderStrong, boxShadow: vt.shadowSm } }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Avatar sx={{ width: 36, height: 36, bgcolor: c.direction === 'inbound' ? vt.blueSoft : vt.greenSoft, color: c.direction === 'inbound' ? vt.blue : 'var(--mexx-green-dark, #558B2F)' }}>
          {c.direction === 'inbound' ? <CallReceivedIcon sx={{ fontSize: 18 }} /> : <CallMadeIcon sx={{ fontSize: 18 }} />}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 700, fontSize: 13.5, color: vt.text }}>{callNumber(c)}</Typography>
            {statusChip(c.status)}
            {c.providerName ? <Box component="span" sx={{ px: 0.7, py: '1px', borderRadius: 0.75, bgcolor: vt.surfaceAlt, border: `1px solid ${vt.border}`, fontSize: 10, fontWeight: 700, color: vt.textSoft }}>{c.providerName}</Box> : null}
          </Box>
          <Typography sx={{ fontSize: 11.5, color: vt.textSoft, mt: 0.25 }}>
            {c.direction === 'inbound' ? f('voip.call.hist.inbound', 'Recebida') : f('voip.call.hist.outbound', 'Realizada')}
            {c.startedAt ? ` · ${fmtWhen(c.startedAt)}` : ''}{c.durationSeconds > 0 ? ` · ${fmtClock(c.durationSeconds)}` : ''} · #{c.id}
          </Typography>
          {c.recordingUrl && (
            <Box sx={{ mt: 1, p: 0.75, bgcolor: vt.surfaceAlt, borderRadius: 2, border: `1px solid ${vt.border}` }}>
              <audio controls preload="none" style={{ width: '100%', height: 34, display: 'block' }} src={c.recordingUrl} />
            </Box>
          )}
        </Box>
        <Tooltip title={f('voip.calls.details', 'Mais informações')}>
          <IconButton size="small" onClick={() => setDetailCall(c)} sx={{ color: vt.blue, flexShrink: 0, '&:hover': { bgcolor: vt.blueSoft } }}><InfoOutlinedIcon sx={{ fontSize: 18 }} /></IconButton>
        </Tooltip>
        {callable && (
          <Tooltip title={f('voip.call.dial', 'Ligar')}>
            <IconButton size="small" onClick={() => dialViaWidget(callNumber(c), null, ticketId || null)} sx={{ bgcolor: vt.greenSoft, color: 'var(--mexx-green-dark, #558B2F)', flexShrink: 0, '&:hover': { bgcolor: 'var(--mexx-green-light, #E4F0D4)' } }}><PhoneIcon sx={{ fontSize: 17 }} /></IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );

  const emptyState = (icon, title, desc) => (
    <Box sx={{ textAlign: 'center', py: 5 }}>
      <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: vt.surfaceAlt, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto' }}>{icon}</Box>
      <Typography sx={{ fontSize: 15, fontWeight: 700, color: vt.text, mt: 1.25 }}>{title}</Typography>
      <Typography sx={{ fontSize: 12.5, color: vt.textSoft }}>{desc}</Typography>
    </Box>
  );

  const segTabs = (
    <Box sx={{ display: 'flex', gap: 0.5, p: 0.4, bgcolor: vt.surfaceAlt, borderRadius: 2.5, mb: 1.75 }}>
      {[
        { k: 'dial', icon: <DialpadIcon sx={{ fontSize: 16 }} />, label: f('voip.call.tab.dial', 'Discar') },
        { k: 'contacts', icon: <PeopleAltIcon sx={{ fontSize: 16 }} />, label: f('voip.call.tab.contacts', 'Contatos') },
        { k: 'recents', icon: <HistoryIcon sx={{ fontSize: 16 }} />, label: f('voip.call.tab.recents', 'Recentes') },
      ].map((t) => {
        const sel = tab === t.k;
        return (
          <Box key={t.k} onClick={() => changeTab(t.k)}
            sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.4, py: 0.75, borderRadius: 2, cursor: 'pointer',
              bgcolor: sel ? vt.surface : 'transparent', boxShadow: sel ? vt.shadowSm : 'none', color: sel ? vt.text : vt.textSoft, fontWeight: sel ? 700 : 600, fontSize: 12.5 }}>
            {t.icon}{t.label}
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 1, md: 0.5 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: 2.5, background: vt.greenGrad, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(85,139,47,.25)' }}>
          <PhoneInTalkIcon sx={{ fontSize: 21, color: '#fff !important' }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 17, color: vt.text, lineHeight: 1.15 }}>{f('voip.call.section.title', 'Telefonia')}</Typography>
          <Typography sx={{ fontSize: 12.5, color: vt.textSoft }}>{f('voip.call.section.subtitle', 'Ligue para o contato e registre as ligações como evidência')}</Typography>
        </Box>
      </Box>

      <Grid container spacing={2.5}>
        {/* ===================== ESQUERDA: HISTÓRICO DO CHAMADO ===================== */}
        <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
          <Box sx={{ ...cardSx, p: 2.5, width: '100%', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 15, color: vt.text }}>{f('voip.call.hist.title', 'Chamadas deste chamado')}{ticketId ? ` #${ticketId}` : ''}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {!loadingHistory && <Box component="span" sx={{ px: 1, py: '2px', borderRadius: 999, bgcolor: vt.surfaceAlt, color: vt.textSoft, fontWeight: 700, fontSize: 11, border: `1px solid ${vt.border}` }}>{history.length}</Box>}
                <Tooltip title={f('voip.call.refresh', 'Atualizar')}>
                  <span>
                    <IconButton size="small" onClick={refreshHistory} disabled={refreshing || loadingHistory} sx={{ color: vt.textSoft, '&:hover': { color: vt.text } }}>
                      {refreshing ? <CircularProgress size={16} sx={{ color: vt.textSoft }} /> : <RefreshIcon sx={{ fontSize: 19 }} />}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>

            {loadingHistory ? skeletonRows(4)
              : history.length === 0 ? emptyState(<PhoneIcon sx={{ fontSize: 30, color: vt.muted }} />, f('voip.call.hist.empty.title', 'Nenhuma chamada ainda'), f('voip.call.hist.empty.desc', 'As ligações feitas ou recebidas neste chamado aparecerão aqui.'))
                : <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>{history.map((c) => callRow(c, false))}</Box>}
          </Box>
        </Grid>

        {/* ===================== DIREITA: BLOCO DO AGENTE (FIXO) ===================== */}
        <Grid item xs={12} md={4}>
          <Box sx={{ position: { md: 'sticky' }, top: 12 }}>
            <Box sx={{ ...cardSx, overflow: 'hidden' }}>
              <Box sx={{ background: vt.greenGrad, p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,.22)', color: '#fff', width: 44, height: 44, fontWeight: 700, fontSize: 18, border: '2px solid rgba(255,255,255,.35)' }}>
                  {String(agentName).charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#fff' }} noWrap>{agentName}</Typography>
                  <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,.9)' }}>{f('voip.call.agentArea', 'Sua central de chamadas')}</Typography>
                </Box>
              </Box>

              <Box sx={{ p: 2 }}>
                {/* contexto do chamado */}
                <Box sx={{ p: 1.25, mb: 2, bgcolor: vt.surfaceAlt, borderRadius: 2.5, border: `1px solid ${vt.border}` }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                    {ticketId && <Box component="span" sx={{ px: 0.85, py: '2px', borderRadius: 1.5, bgcolor: vt.blueSoft, color: vt.blue, fontWeight: 700, fontSize: 11 }}>#{ticketId}</Box>}
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: vt.text }} noWrap>{ticketTitle || f('voip.call.ticket', 'Chamado')}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: vt.greenSoft, color: 'var(--mexx-green-dark, #558B2F)', fontSize: 12, fontWeight: 700 }}>{String(contactName).charAt(0).toUpperCase()}</Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: vt.text, flex: 1, minWidth: 0 }} noWrap>{contactName}</Typography>
                        {contactPhone && <Typography sx={{ fontSize: 11.5, color: vt.textSoft, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{contactPhone}</Typography>}
                        {contactPhoneValid && voipState.hasRamal && (
                          <Tooltip title={f('voip.call.dial', 'Ligar')}>
                            <IconButton size="small" onClick={() => setDialConfirm({ open: true, number: contactPhone })} sx={{ width: 26, height: 26, bgcolor: vt.greenSoft, color: 'var(--mexx-green-dark, #558B2F)', flexShrink: 0, '&:hover': { bgcolor: 'var(--mexx-green-light, #E4F0D4)' } }}><PhoneIcon sx={{ fontSize: 15 }} /></IconButton>
                          </Tooltip>
                        )}
                      </Box>
                      {contactOrg && <Typography sx={{ fontSize: 11, color: vt.textSoft }} noWrap>{contactOrg}</Typography>}
                    </Box>
                  </Box>
                </Box>

                {/* meu ramal */}
                <Typography sx={{ fontSize: 11, color: vt.muted, fontWeight: 700, letterSpacing: 0.4, mb: 0.75 }}>{f('voip.call.myExtension', 'MEU RAMAL')}</Typography>
                {loadingExt ? <Skeleton variant="rounded" height={40} sx={{ mb: 2 }} />
                  : extensions.length === 0 ? (
                    <Box sx={{ p: 1.25, mb: 2, borderRadius: 2, bgcolor: vt.surfaceAlt, border: `1px solid ${vt.border}` }}>
                      <Typography sx={{ fontSize: 12, color: vt.textSoft }}>{f('voip.call.noRamal', 'Você não tem um ramal configurado.')}</Typography>
                    </Box>
                  ) : (
                    <Autocomplete options={extensions} value={ramal} disableClearable size="small"
                      getOptionLabel={(o) => `${o.ext}${o.name ? ` · ${o.name}` : ''}${o.provider ? ` (${o.provider})` : ''}`} isOptionEqualToValue={(o, v) => o.id === (v && v.id)}
                      onChange={(e, v) => v && setRamal(v)} sx={{ mb: 2 }}
                      renderOption={(props, o) => (
                        <Box component="li" {...props} key={o.id} sx={{ display: 'flex', alignItems: 'center' }}>
                          <span style={{ flex: 1 }}>{o.ext}{o.name ? ` · ${o.name}` : ''}</span>
                          {o.provider ? <Box sx={{ ml: 1, px: 0.75, py: 0.15, borderRadius: 1, bgcolor: vt.chipBg || '#eef2f7', border: `1px solid ${vt.border}`, fontSize: 10.5, fontWeight: 700, color: vt.textSoft }}>{o.provider}</Box> : null}
                        </Box>
                      )}
                      renderInput={(params) => <TextField {...params} placeholder={f('voip.call.searchExt', 'Buscar ramal')}
                        InputProps={{ ...params.InputProps, startAdornment: <DialpadIcon sx={{ fontSize: 17, color: vt.muted, ml: 0.5, mr: 0.5 }} /> }} />} />
                  )}

                {segTabs}

                {/* área de altura fixa — troca de aba não empurra a tela */}
                <Box sx={{ height: 296, display: 'flex', flexDirection: 'column', mt: 0.5 }}>
                  {tab === 'dial' && (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <PhoneInput country={'br'} value={number} onChange={(v, c) => { setNumber(v); setCountry(c); }}
                            countryCodeEditable={false} enableSearch inputStyle={{ width: '100%', height: 46, fontSize: 15, border: `1px solid ${vt.border}`, borderRadius: 12 }} specialLabel="" />
                        </Box>
                        <Tooltip title={f('voip.call.dial', 'Ligar')}>
                          <IconButton onClick={() => dialViaWidget(applyDdi(number, country, useDdi), { name: contactName, org: contactOrg }, ticketId || null)}
                            sx={{ width: 46, height: 46, flexShrink: 0, background: vt.greenGrad, color: '#fff', borderRadius: 2.5, boxShadow: '0 6px 16px rgba(85,139,47,.28)', '&:hover': { background: vt.greenGradHover } }}>
                            <PhoneIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <FormControlLabel sx={{ mt: 0.5, ml: 0 }}
                        control={<Switch checked={useDdi} onChange={(e) => setUseDdi(e.target.checked)} size="small" />}
                        label={<Typography sx={{ fontSize: 12.5, color: vt.textSoft }}>{f('voip.call.useDdi', 'Usar DDI')}</Typography>} />
                    </Box>
                  )}

                  {tab === 'contacts' && contactsContent()}

                  {tab === 'recents' && (
                    <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.5 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: vt.muted, letterSpacing: 0.5 }}>{f('voip.call.tab.recents', 'Recentes')}</Typography>
                        <Tooltip title={f('voip.call.refresh', 'Atualizar')}>
                          <span>
                            <IconButton size="small" onClick={loadRecents} disabled={loadingRecents} sx={{ color: vt.textSoft, '&:hover': { color: vt.green, bgcolor: vt.greenSoft } }}>
                              <RefreshIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                      {loadingRecents ? skeletonRows(5)
                        : recents.length === 0 ? emptyState(<HistoryIcon sx={{ fontSize: 30, color: vt.muted }} />, f('voip.call.recents.empty.title', 'Sem ligações recentes'), f('voip.call.recents.empty.desc', 'Suas ligações recentes aparecerão aqui.'))
                          : recents.map((c) => callRow(c, true))}
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <VoipCallDetailsModal call={detailCall} onClose={() => setDetailCall(null)} />
      <VoipDialConfirm open={dialConfirm.open} number={dialConfirm.number} providerName={voipState.ramal && voipState.ramal.provider}
        onClose={() => setDialConfirm({ open: false, number: '' })}
        onConfirm={() => { dialViaWidget(dialConfirm.number, { name: contactName, org: contactOrg }, ticketId || null); setDialConfirm({ open: false, number: '' }); }} />
    </Box>
  );
}

const mapStateToProps = (state) => ({ calleds: state.calleds, userSession: state.userSession });
export default injectIntl(connect(mapStateToProps)(withStyles(Styles)(VoipCallPanel)));
